import type { SpUser } from "@/interfaces/IEquipe"
import EquipeService from "@/service/EquipeService"
import { defineStore } from "pinia"
import { ref } from "vue"

export const useEquipeStore = defineStore(
  "equipe",
  () => {
    const equipeService = new EquipeService()
    const equipe = ref<SpUser[]>([])
    const loadingStates = ref<Set<string>>(new Set())
    const lastFetchTime = ref<number>(0)

    // Cache TTL de 5 minutes
    const CACHE_TTL = 5 * 60 * 1000

    const isCacheValid = (): boolean => {
      return Date.now() - lastFetchTime.value < CACHE_TTL
    }

    async function getEquipe(forceRefresh = false): Promise<SpUser[]> {
      if (!forceRefresh && equipe.value.length > 0 && isCacheValid()) {
        return equipe.value
      }

      try {
        equipe.value = await equipeService.get()
        lastFetchTime.value = Date.now()
        return equipe.value
      } catch (error) {
        console.error("Erreur lors de la récupération de l'équipe:", error)
        throw error
      }
    }

    async function getSpUser(Id: string): Promise<SpUser> {
      // Vérifier d'abord dans le cache local
      let spUser = equipe.value.find((u) => u.Id === Id)
      if (spUser) {
        return spUser
      }

      // Éviter les appels duplicatas
      if (loadingStates.value.has(Id)) {
        // Attendre que l'appel en cours se termine
        const checkLoading = (): Promise<SpUser> => {
          return new Promise((resolve, reject) => {
            const interval = setInterval(() => {
              if (!loadingStates.value.has(Id)) {
                clearInterval(interval)
                const user = equipe.value.find((u) => u.Id === Id)
                if (user) {
                  resolve(user)
                } else {
                  reject(
                    new Error(`Utilisateur ${Id} non trouvé après chargement`),
                  )
                }
              }
            }, 50)
          })
        }
        return checkLoading()
      }

      loadingStates.value.add(Id)

      try {
        spUser = await equipeService.getSpUser(Id)
        equipe.value.push(spUser)
        return spUser
      } catch (error) {
        console.error(
          `Erreur lors de la récupération de l'utilisateur ${Id}:`,
          error,
        )
        throw error
      } finally {
        loadingStates.value.delete(Id)
      }
    }

    async function getSpUsers(Ids: string[]): Promise<SpUser[]> {
      if (Ids.length === 0) return []

      // Séparer les utilisateurs déjà en cache de ceux à récupérer
      const cached: SpUser[] = []
      const toFetch: string[] = []

      for (const id of Ids) {
        const cachedUser = equipe.value.find((u) => u.Id === id)
        if (cachedUser) {
          cached.push(cachedUser)
        } else if (!loadingStates.value.has(id)) {
          toFetch.push(id)
        }
      }

      let fetchedUsers: SpUser[] = []

      if (toFetch.length > 0) {
        // Marquer comme en cours de chargement
        toFetch.forEach((id) => loadingStates.value.add(id))

        try {
          fetchedUsers = await equipeService.getSpUsers(toFetch)
          // Ajouter au cache
          equipe.value.push(...fetchedUsers)
        } catch (error) {
          console.error(
            "Erreur lors de la récupération des utilisateurs multiples:",
            error,
          )
          throw error
        } finally {
          // Nettoyer les états de chargement
          toFetch.forEach((id) => loadingStates.value.delete(id))
        }
      }

      return [...cached, ...fetchedUsers]
    }

    function clearCache(): void {
      equipe.value = []
      lastFetchTime.value = 0
      loadingStates.value.clear()
    }

    return {
      equipe,
      getEquipe,
      getSpUser,
      getSpUsers,
      clearCache,
      isCacheValid: () => isCacheValid(),
    }
  },
  {
    persist: {
      storage: sessionStorage,
    },
  },
)
